import { API_URL } from "../config";
import { resolveLaunchContext } from "../telegram/launch";

/** A pointer to an uploaded voice note, as carried on a chat message. */
export interface VoiceRef {
  voiceId: string;
  durationMs: number;
}

/** A live recording session — framework-free, so the UI just drives stop/cancel. */
export interface VoiceRecorder {
  /** Stop, flush the encoder, release the mic, and resolve the recorded clip. */
  stop(): Promise<{ blob: Blob; mime: string; durationMs: number }>;
  /** Abort without producing a clip; always releases the mic. */
  cancel(): void;
  /** Live input level, present when WebAudio could tap the mic stream:
   * `read()` → the current time-domain peak amplitude, 0..1. Absent (older
   * WebViews without `AudioContext`) the recording UI falls back to its
   * static pulse indicator. */
  meter?: { read(): number };
}

/** Hard cap on a voice note (matches the api contract: `durationMs ≤ 60000`). */
export const MAX_VOICE_MS = 60_000;

/** Preferred container/codec order; the first the browser supports wins. */
const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"] as const;

/**
 * The same initData source the api client uses (`resolveLaunchContext().initDataRaw`,
 * wired through `main.tsx`). Resolved lazily + defensively so importing this module
 * never touches the Telegram SDK until a voice note is actually sent/played, and a
 * non-Telegram context (dev, tests) just yields `null` (no `Authorization` header).
 */
let cachedInitData: string | null | undefined;
function currentInitData(): string | null {
  if (cachedInitData === undefined) {
    try {
      cachedInitData = resolveLaunchContext().initDataRaw;
    } catch {
      cachedInitData = null;
    }
  }
  return cachedInitData;
}

function authHeaders(extra?: Record<string, string>): Headers {
  const headers = new Headers(extra);
  const initData = currentInitData();
  if (initData) headers.set("Authorization", `tma ${initData}`);
  return headers;
}

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const faNum = (s: string): string => s.replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);

/** mm:ss for a millisecond duration (voice bubble + the live recording timer),
 *  in PERSIAN digits like every other number in the app. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return faNum(`${mm}:${ss.toString().padStart(2, "0")}`);
}

/**
 * Whether this WebView can record: needs both `MediaRecorder` and
 * `getUserMedia`. Some iOS Telegram WebViews lack one or both — the mic button is
 * hidden when this is false, so text chat still works everywhere.
 */
export function isVoiceNoteSupported(): boolean {
  return (
    typeof MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function"
  );
}

function pickMimeType(): string {
  if (typeof MediaRecorder !== "undefined" && typeof MediaRecorder.isTypeSupported === "function") {
    for (const candidate of MIME_CANDIDATES) {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate;
    }
  }
  return "";
}

/* ------------------------------------------------------------------ *
 * Waveform support (client-only; the wire payload never changes):
 *  - recording: an AnalyserNode taps the SAME mic stream the recorder
 *    encodes, so the composer can paint live amplitude bars;
 *  - playback: the downloaded clip is decoded once and downsampled to a
 *    fixed number of peak buckets for the bubble's waveform.
 * ------------------------------------------------------------------ */

/** Peak buckets per playback waveform (the bubble renders one bar each). */
export const WAVEFORM_BUCKETS = 48;

/** `AudioContext` if this WebView has WebAudio (incl. the webkit- prefix). */
function audioContextCtor(): (new () => AudioContext) | null {
  const g = globalThis as { AudioContext?: new () => AudioContext } & {
    webkitAudioContext?: new () => AudioContext;
  };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

/**
 * Time-domain peak amplitude of one analyser frame (`getByteTimeDomainData`
 * bytes, centered on 128), normalized 0..1. Pure — unit-tested directly.
 */
export function amplitudeOf(bytes: ArrayLike<number>): number {
  let peak = 0;
  for (let i = 0; i < bytes.length; i++) {
    const deviation = Math.abs((bytes[i] ?? 128) - 128);
    if (deviation > peak) peak = deviation;
  }
  return Math.min(1, peak / 128);
}

/**
 * Downsample an amplitude series (a decoded PCM channel, or anything
 * array-like) into `buckets` peak values, normalized so the loudest bucket
 * is 1. A silent/empty input yields all-zero buckets (the UI applies its own
 * minimum bar height). Pure — unit-tested directly.
 */
export function downsamplePeaks(samples: ArrayLike<number>, buckets: number): number[] {
  const count = Math.max(1, Math.floor(buckets));
  const n = samples.length;
  const out = new Array<number>(count).fill(0);
  if (n === 0) return out;
  for (let b = 0; b < count; b++) {
    const start = Math.floor((b * n) / count);
    const end = Math.max(start + 1, Math.floor(((b + 1) * n) / count));
    let peak = 0;
    for (let i = start; i < end && i < n; i++) {
      const v = Math.abs(samples[i] ?? 0);
      if (v > peak) peak = v;
    }
    out[b] = peak;
  }
  const max = Math.max(...out);
  return max > 0 ? out.map((v) => v / max) : out;
}

/** A live mic-level meter over WebAudio; `null` when WebAudio is unavailable
 * (the recording UI then keeps its pulse-dot fallback). Never throws. */
function createStreamMeter(stream: MediaStream): { read(): number; dispose(): void } | null {
  try {
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    const ctx = new Ctor();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const bytes = new Uint8Array(analyser.fftSize);
    return {
      read: () => {
        analyser.getByteTimeDomainData(bytes);
        return amplitudeOf(bytes);
      },
      dispose: () => {
        void Promise.resolve(ctx.close()).catch(() => undefined);
      },
    };
  } catch {
    return null;
  }
}

/**
 * Start recording from the mic. Chooses the best supported mime, collects chunks,
 * measures duration with `performance.now()`, and auto-stops at {@link MAX_VOICE_MS}.
 * The returned handle always releases the media tracks on `stop()`/`cancel()`.
 */
/** Best-effort read of the mic permission ("granted" / "denied" / "prompt"), or null when
 *  the Permissions API can't answer for the microphone — common in Telegram WebViews (iOS
 *  Safari/WKWebView don't expose it), where we then just show the pre-prompt once. */
export async function queryMicPermission(): Promise<PermissionState | null> {
  try {
    const perms = navigator.permissions;
    if (!perms?.query) return null;
    const status = await perms.query({ name: "microphone" as PermissionName });
    return status.state;
  } catch {
    return null;
  }
}

export async function startVoiceRecording(): Promise<VoiceRecorder> {
  // Open the mic ONLY for THIS recording — a fresh stream each time — and stop its tracks
  // the instant recording ends (stop/cancel below), so the OS mic indicator goes dark
  // between notes. We deliberately do NOT hold the stream across recordings: users must
  // never feel the mic is left listening; the occasional extra native prompt (WebViews
  // don't persist the grant) is the accepted trade — a branded pre-prompt softens it.
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = pickMimeType();
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  // Live level meter for the recording waveform — best-effort (null without
  // WebAudio); it taps the stream and never touches the encoded clip.
  const meter = createStreamMeter(stream);

  const chunks: Blob[] = [];
  recorder.addEventListener("dataavailable", (event) => {
    if (event.data && event.data.size > 0) chunks.push(event.data);
  });

  // Resolves once the encoder has fully flushed (chunks are final).
  const stopped = new Promise<void>((resolve) => {
    recorder.addEventListener("stop", () => resolve(), { once: true });
  });

  const startedAt = performance.now();
  recorder.start();

  // Stop the mic tracks — releasing the OS mic, indicator OFF — the moment recording ends.
  const stopTracks = (): void => {
    for (const track of stream.getTracks()) track.stop();
  };

  // Safety cap: stop the encoder at the max; the clip is finalized on stop().
  const autoStop = setTimeout(() => {
    if (recorder.state !== "inactive") recorder.stop();
  }, MAX_VOICE_MS);

  const build = (): { blob: Blob; mime: string; durationMs: number } => {
    const mime = recorder.mimeType || mimeType || "audio/webm";
    return {
      blob: new Blob(chunks, { type: mime }),
      mime,
      durationMs: Math.min(MAX_VOICE_MS, Math.round(performance.now() - startedAt)),
    };
  };

  return {
    stop: async () => {
      clearTimeout(autoStop);
      meter?.dispose();
      if (recorder.state !== "inactive") recorder.stop();
      await stopped;
      stopTracks(); // mic indicator off immediately
      return build();
    },
    cancel: () => {
      clearTimeout(autoStop);
      meter?.dispose();
      if (recorder.state !== "inactive") recorder.stop();
      stopTracks();
    },
    ...(meter ? { meter: { read: meter.read } } : {}),
  };
}

/**
 * Upload a recorded clip to the api (`POST /voice?durationMs=…`, the raw Blob as the
 * body) and return the stored note's id + server-clamped duration. Throws on a
 * non-2xx response so the composer can surface the failure.
 */
export async function uploadVoiceNote(blob: Blob, durationMs: number): Promise<VoiceRef> {
  const headers = authHeaders({ "Content-Type": blob.type || "application/octet-stream" });
  const res = await fetch(`${API_URL}/voice?durationMs=${Math.round(durationMs)}`, {
    method: "POST",
    headers,
    body: blob,
  });
  if (!res.ok) throw new Error(`Voice upload failed (${res.status})`);
  const data = (await res.json()) as { id: string; durationMs: number };
  return { voiceId: data.id, durationMs: data.durationMs };
}

/**
 * Download a voice note on demand — the shared byte source for BOTH the
 * playable object URL and the waveform decode, so a clip is fetched exactly
 * once. Memoized per id (the *promise* is cached, so concurrent callers share
 * a single download); a failed fetch is evicted so a later play can retry.
 */
const blobCache = new Map<string, Promise<Blob>>();
function voiceNoteBlob(id: string): Promise<Blob> {
  const cached = blobCache.get(id);
  if (cached) return cached;

  const promise = (async () => {
    const res = await fetch(`${API_URL}/voice/${encodeURIComponent(id)}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Voice download failed (${res.status})`);
    return res.blob();
  })();

  blobCache.set(id, promise);
  void promise.catch(() => blobCache.delete(id));
  return promise;
}

/** A playable object URL for a voice note (downloads through the shared blob
 * cache; the URL itself is also memoized per id, and failures are evicted). */
const objectUrlCache = new Map<string, Promise<string>>();
export function voiceNoteObjectUrl(id: string): Promise<string> {
  const cached = objectUrlCache.get(id);
  if (cached) return cached;

  const promise = voiceNoteBlob(id).then((blob) => URL.createObjectURL(blob));
  objectUrlCache.set(id, promise);
  void promise.catch(() => objectUrlCache.delete(id));
  return promise;
}

/**
 * Decode a voice clip's raw bytes into normalized waveform peaks:
 * `decodeAudioData` then downsample the first channel to `buckets` buckets.
 * Throws when the WebView has no WebAudio (or can't read the codec), so the
 * caller keeps its slim-bar fallback. Cache-agnostic — the id-keyed memoization
 * lives in {@link voiceNotePeaks} / {@link primeVoiceNotePeaks} below.
 */
async function decodeClipPeaks(blob: Blob, buckets: number): Promise<number[]> {
  const Ctor = audioContextCtor();
  if (!Ctor) throw new Error("WebAudio unavailable");
  const buf = await blob.arrayBuffer();
  const ctx = new Ctor();
  try {
    const audio = await decodeAudio(ctx, buf);
    return downsamplePeaks(audio.getChannelData(0), buckets);
  } finally {
    void Promise.resolve(ctx.close()).catch(() => undefined);
  }
}

/** Per-id waveform-peak cache (the *promise*), shared by the fetch + prime paths
 * so a clip is decoded exactly once; failures are evicted so a later attempt can
 * retry. */
const peaksCache = new Map<string, Promise<number[]>>();

/**
 * Waveform peaks for a voice note: fetch the clip (shared with playback) then
 * {@link decodeClipPeaks}. Cached per id in-memory so re-renders/repeat plays
 * never re-decode; a failure is evicted (and REJECTS — the bubble keeps its
 * simple progress-bar fallback, e.g. WebViews without WebAudio or a codec
 * `decodeAudioData` can't read).
 *
 * The bubble calls this eagerly on LOAD so the real waveform paints before the
 * user ever plays. When the sender already seeded the peaks from the recorded
 * blob ({@link primeVoiceNotePeaks}), this returns that cached promise with no
 * network at all.
 */
export function voiceNotePeaks(id: string, buckets = WAVEFORM_BUCKETS): Promise<number[]> {
  const cached = peaksCache.get(id);
  if (cached) return cached;

  const promise = voiceNoteBlob(id).then((blob) => decodeClipPeaks(blob, buckets));
  peaksCache.set(id, promise);
  void promise.catch(() => peaksCache.delete(id));
  return promise;
}

/**
 * Seed the caches for a JUST-RECORDED note from its local blob, so the sender's
 * OWN bubble paints the real waveform the instant it appears (and plays back
 * from this blob — no download round-trip). Best-effort + idempotent: the peak
 * decode is memoized under the id exactly like {@link voiceNotePeaks} (a later
 * bubble mount for the same id reuses it), and a decode failure is evicted so
 * the bubble can still lazily fetch. Never throws synchronously.
 */
export function primeVoiceNotePeaks(
  id: string,
  blob: Blob,
  buckets = WAVEFORM_BUCKETS,
): Promise<number[]> {
  // Reuse the recorded bytes for playback too (skip downloading our own clip).
  if (!blobCache.has(id)) blobCache.set(id, Promise.resolve(blob));

  const cached = peaksCache.get(id);
  if (cached) return cached;

  const promise = decodeClipPeaks(blob, buckets);
  peaksCache.set(id, promise);
  void promise.catch(() => peaksCache.delete(id));
  return promise;
}

/** `decodeAudioData` across both signatures (older WebKit is callback-only). */
function decodeAudio(ctx: AudioContext, buf: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    const result = ctx.decodeAudioData(buf, resolve, reject) as Promise<AudioBuffer> | undefined;
    if (result && typeof result.then === "function") result.then(resolve, reject);
  });
}
